// VideoCard.jsx

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Services
import { resolveImageUrl, resolvePlayableVideoUrl } from 'services/storage/StorageAccessService';

// Hooks
import useGalleryCardLoadGate from 'views/gallery/hooks/useGalleryCardLoadGate';

// Utilities
import resolveMediaAspectRatio from 'views/gallery/utils/resolveMediaAspectRatio';

// CSS
import './VideoCard.css';

const GALLERY_VIDEO_PLAY_ROOT_MARGIN = '200px';

/**
 * VideoCard
 *
 * Gallery video thumbnail with reserved aspect ratio and loading shimmer.
 *
 * @param {object} node - Video node to display.
 * @param {function} [onAspectRatio] - Called with measured width/height ratio.
 * @param {boolean} [reserveAspectRatio=true] - When true, sizes the container from
 *   node metadata (or a measured ratio) to avoid layout jump while loading.
 *
 * @returns {ReactNode} The rendered component.
 */
const VideoCard = ({ node, onAspectRatio, reserveAspectRatio = true }) => {

    // ----- REFS ----- //

    const videoRef = useRef(null);

    // ----- STATE ----- //

    // Playable video URL.
    const [videoSrc, setVideoSrc] = useState(null);

    // Poster/cover image URL.
    const [posterUrl, setPosterUrl] = useState(null);

    // Whether the video element is near the viewport enough to autoplay.
    const [isVisible, setIsVisible] = useState(false);

    // Whether poster or video metadata is ready to show.
    const [isReady, setIsReady] = useState(false);

    // Ratio measured from loaded video metadata.
    const [measuredRatio, setMeasuredRatio] = useState(null);

    const rawCover = typeof node?.coverUrl === 'string' ? node.coverUrl.trim() : '';
    const storagePath = node?.coverStoragePath || null;
    const loadKey = `${node?.key || ''}:${rawCover}:${storagePath || ''}`;

    // ----- MEMOS ----- //

    // Reserve layout from node metadata until the video reports its own ratio.
    const aspectRatio = useMemo(
        () => measuredRatio || resolveMediaAspectRatio(node),
        [measuredRatio, node?.width, node?.height, node?.aspectRatio],
    );

    // ----- HOOKS ----- //

    const { containerRef, shouldLoad } = useGalleryCardLoadGate(loadKey);

    // ----- EFFECTS ----- //

    // Reset paint state when the node identity changes.
    useLayoutEffect(() => {
        setVideoSrc(null);
        setPosterUrl(null);
        setIsVisible(false);
        setIsReady(false);
        setMeasuredRatio(null);
    }, [loadKey]);

    // Autoplay only while the video is sufficiently visible.
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl || typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target !== videoEl) return;
                    setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.25);
                });
            },
            {
                root: null,
                rootMargin: GALLERY_VIDEO_PLAY_ROOT_MARGIN,
                threshold: [0, 0.25, 0.5, 0.75, 1],
            },
        );

        observer.observe(videoEl);

        return () => {
            observer.disconnect();
        };
    }, [loadKey, shouldLoad]);

    // Resolve the playable video URL once near the viewport.
    useEffect(() => {
        let cancelled = false;

        if (!shouldLoad) {
            setVideoSrc(null);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const url = await resolvePlayableVideoUrl(node);
            if (cancelled) return;
            setVideoSrc(url || null);
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldLoad, node]);

    // Resolve the poster/cover URL once near the viewport.
    useEffect(() => {
        let cancelled = false;

        if (!shouldLoad) {
            setPosterUrl(null);
            return () => {
                cancelled = true;
            };
        }

        if (!rawCover && !storagePath) {
            setPosterUrl(null);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const url = await resolveImageUrl({
                nodeKey: node?.key || null,
                url: rawCover || null,
                storagePath,
            });
            if (cancelled) return;
            setPosterUrl(url || rawCover || null);
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldLoad, node?.key, rawCover, storagePath]);

    // Treat an available poster as enough to dismiss the shimmer.
    useEffect(() => {
        if (posterUrl) {
            setIsReady(true);
        }
    }, [posterUrl]);

    // Play/pause based on visibility.
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl || !videoSrc) return;

        if (isVisible) {
            const playPromise = videoEl.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {
                    /* noop: autoplay may be prevented */
                });
            }
        } else {
            videoEl.pause();
        }
    }, [isVisible, videoSrc]);

    if (!node) return null;

    // ----- RENDER ----- //

    return (
        <div
          className="video-card-container"
          ref={containerRef}
          style={reserveAspectRatio ? {
                aspectRatio: String(aspectRatio),
            } : undefined}
        >

            {/* SHIMMER */}
            {!isReady && (
                <div
                  aria-hidden="true"
                  className="video-card-shimmer"
                />
            )}

            {/* VIDEO */}
            <video
              className="video-card-video"
              style={{
                    opacity: isReady ? 1 : 0,
                }}
              ref={videoRef}
              src={videoSrc || undefined}
              poster={posterUrl || undefined}
              loop
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(event) => {
                    const el = event?.currentTarget;
                    if (el?.videoWidth && el?.videoHeight) {
                        const ratio = el.videoWidth / el.videoHeight;
                        setMeasuredRatio(ratio);
                        onAspectRatio?.(node, ratio);
                    }
                    setIsReady(true);
                }}
            />
        </div>
    );
};

// Dev Tools
VideoCard.displayName = 'VideoCard';

export default VideoCard;
